import React, { useEffect, useMemo, useState } from "react";
import { Container, Form } from "react-bootstrap";
import {
  Activity,
  BatteryMedium,
  ChevronDown,
  Cpu,
  Gauge,
  MapPin,
  Radio,
  Waves,
  Wifi,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import api from "../api/api";
import LoadingSpinner from "../components/common/LoadingSpinner";
import SensorEnvironmentStatus from "../components/common/SensorEnvironmentStatus";
import useSensorData from "../utils/useSensorData";
import { getSensorDepths } from "../utils/sensorDepths";
import DBSensor from "./DBSensor";
import drop from "../assets/images/drop.png";
import drop1 from "../assets/images/drop1.png";
import drop2 from "../assets/images/drop2.png";
import humidity from "../assets/images/humidity.png";
import press from "../assets/images/press.png";
import sensorProduct from "../assets/images/smartfarm_capteur.png";
import temp from "../assets/images/temp.png";
import "./Sensors.css";

const EMPTY_SENSOR = {
  id: "",
  code: "",
  time: "",
  charge: "",
  signal: "",
  temp: "",
  humidity: "",
  pressure: "",
  humidityMV1: "",
  humidityMV2: "",
  humidityMV3: "",
  environment: null,
};

const hasValue = (value) =>
  value !== "" && value !== null && value !== undefined;

const SensorMetric = ({ icon, label, value, accent = "teal" }) => (
  <article className={`sensor-detail-metric sensor-detail-metric--${accent}`}>
    <span className="sensor-detail-metric-icon" aria-hidden="true">
      <img src={icon} alt="" />
    </span>
    <div>
      <span className="sensor-detail-metric-label">{label}</span>
      <strong className="sensor-detail-metric-value">
        {hasValue(value) ? value : "—"}
      </strong>
    </div>
  </article>
);

const formatSoilMoisture = (value) => {
  if (!hasValue(value)) return "—";

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
};

const SoilDepthVisualization = ({ metrics, t }) => (
  <div className="sensor-soil-visual">
    <div className="sensor-soil-device" aria-hidden="true">
      <span className="sensor-soil-device-glow" />
      <span className="sensor-soil-ground" />
      <img src={sensorProduct} alt="" />
      {metrics.map((metric, index) => (
        <span
          key={`sensor-marker-${index}-${metric.depth}`}
          className={`sensor-soil-device-marker sensor-soil-device-marker--${
            index + 1
          }`}
        />
      ))}
    </div>

    <div className="sensor-soil-levels">
      <div className="sensor-soil-levels-intro">
        <strong>S.sensor</strong>
        <span>
          {t("soil_monitoring_levels", {
            defaultValue: "Mesure en temps réel sur 3 profondeurs",
          })}
        </span>
      </div>

      {metrics.map((metric, index) => (
        <article
          key={`sensor-level-${index}-${metric.depth}`}
          className={`sensor-soil-level sensor-soil-level--${index + 1}`}
        >
          <span className="sensor-soil-level-icon" aria-hidden="true">
            <img src={metric.icon} alt="" />
          </span>
          <div className="sensor-soil-level-copy">
            <span>
              {t("soil_depth_level", {
                defaultValue: "Niveau {{level}} · {{depth}} cm",
                level: index + 1,
                depth: metric.depth,
              })}
            </span>
            <strong>
              {formatSoilMoisture(metric.value)}
              {hasValue(metric.value) && <small>%</small>}
            </strong>
          </div>
          <span className="sensor-soil-level-status">
            {hasValue(metric.value)
              ? t("live", { defaultValue: "Live" })
              : t("waiting", { defaultValue: "En attente" })}
          </span>
        </article>
      ))}
    </div>
  </div>
);

export default function Sensors() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: routeSensorCode = "" } = useParams();
  const [dataSensors, setDataSensors] = useState([]);
  const [dataFields, setDataFields] = useState([]);
  const [searchCode, setSearchCode] = useState(routeSensorCode);
  const [fieldId, setFieldId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [dataSensor, setDataSensor] = useState(EMPTY_SENSOR);

  useEffect(() => {
    let requestIsCurrent = true;

    const loadPageData = async () => {
      setIsLoading(true);
      try {
        const [sensorsResponse, fieldsResponse] = await Promise.all([
          api.get("/sensor/sensors"),
          api.get("/field/fields"),
        ]);

        if (!requestIsCurrent) return;

        const sensors = Array.isArray(sensorsResponse.data)
          ? sensorsResponse.data
          : [];
        const farms = Array.isArray(fieldsResponse.data?.farms)
          ? fieldsResponse.data.farms
          : [];
        const selectedCode = routeSensorCode || sensors[0]?.code || "";
        const selectedSensor = sensors.find(
          (sensor) => sensor.code === selectedCode
        );

        setDataSensors(sensors);
        setDataFields(farms);
        setSearchCode(selectedCode);
        setFieldId(
          selectedSensor?.fields?.uid || selectedSensor?.field?.uid || ""
        );
      } catch (error) {
        console.error("Error loading sensor page:", error);
      } finally {
        if (requestIsCurrent) setIsLoading(false);
      }
    };

    loadPageData();

    return () => {
      requestIsCurrent = false;
    };
  }, [routeSensorCode]);

  const fields = useMemo(
    () =>
      dataFields.flatMap((farm) =>
        Array.isArray(farm.fields) ? farm.fields : []
      ),
    [dataFields]
  );

  const selectedFieldName = useMemo(() => {
    const selectedField = fields.find((field) =>
      Array.isArray(field.sensors)
        ? field.sensors.some((sensor) => sensor.code === searchCode)
        : false
    );
    return selectedField?.name || "—";
  }, [fields, searchCode]);

  const sensorsData = useSensorData({ fieldId });
  const sensorDepths = useMemo(
    () => getSensorDepths(dataSensors.find((sensor) => sensor.code === searchCode)),
    [dataSensors, searchCode]
  );

  useEffect(() => {
    const currentReading = sensorsData.find(
      (reading) => reading.code === searchCode
    );

    if (!currentReading) {
      setDataSensor(EMPTY_SENSOR);
      return;
    }

    setDataSensor({
      id: currentReading.sensor_id,
      code: currentReading.code,
      time: currentReading.time,
      charge: currentReading.charge,
      signal: currentReading.signal,
      temp: currentReading.temperature,
      humidity: currentReading.humidity,
      pressure: hasValue(currentReading.pressure)
        ? Number(currentReading.pressure) / 1000
        : "",
      humidityMV1: currentReading.mv1,
      humidityMV2: currentReading.mv2,
      humidityMV3: currentReading.mv3,
      environment: currentReading.environment || null,
    });
  }, [sensorsData, searchCode]);

  const handleSensorChange = (event) => {
    const code = event.target.value;
    const selectedSensor = dataSensors.find((sensor) => sensor.code === code);

    setSearchCode(code);
    setFieldId(selectedSensor?.fields?.uid || selectedSensor?.field?.uid || "");
    if (code) navigate(`/Sensors/${encodeURIComponent(code)}`);
  };

  const airMetrics = [
    {
      label: `${t("Temp.")} (°C)`,
      value: dataSensor.temp,
      icon: temp,
      accent: "coral",
    },
    {
      label: `${t("Humidity")} (%)`,
      value: dataSensor.humidity,
      icon: humidity,
      accent: "teal",
    },
    {
      label: `${t("Pression")} (kPa)`,
      value: hasValue(dataSensor.pressure)
        ? Number(dataSensor.pressure).toFixed(2)
        : "",
      icon: press,
      accent: "amber",
    },
  ];

  const soilMetrics = [
    { depth: sensorDepths.mv1, value: dataSensor.humidityMV1, icon: drop },
    { depth: sensorDepths.mv2, value: dataSensor.humidityMV2, icon: drop2 },
    { depth: sensorDepths.mv3, value: dataSensor.humidityMV3, icon: drop1 },
  ];

  const hasLiveReading = Boolean(dataSensor.code);

  return (
    <Container fluid className="sensor-detail-page">
      <header className="sensor-detail-header">
        <div className="sensor-detail-heading">
          <span className="sensor-detail-eyebrow">SMARTFARM</span>
          <h1>{t("sensors")}</h1>
          <p>
            {t("view_sensors_data_history", {
              defaultValue:
                "Monitor live environmental readings and soil moisture levels.",
            })}
          </p>
        </div>

        <div className="sensor-detail-header-tools">
          <label htmlFor="sensor-detail-select">
            <Cpu size={15} aria-hidden="true" />
            {t("sensor_code")}
          </label>
          <div className="sensor-detail-select-wrap">
            <Form.Select
              id="sensor-detail-select"
              value={searchCode}
              onChange={handleSensorChange}
              aria-label={t("sensor_code")}
            >
              {!searchCode && <option value="">{t("sensor_code")}</option>}
              {dataSensors.map((sensor) => (
                <option key={sensor.code} value={sensor.code}>
                  {sensor.code}
                </option>
              ))}
            </Form.Select>
            <ChevronDown size={16} aria-hidden="true" />
          </div>
        </div>
      </header>

      <section className="sensor-detail-context" aria-label={t("overview")}>
        <div className="sensor-detail-context-primary">
          <span className="sensor-detail-device-icon">
            <Radio size={22} aria-hidden="true" />
          </span>
          <div>
            <span>{t("sensor_code")}</span>
            <strong>{searchCode || "—"}</strong>
          </div>
        </div>
        <div className="sensor-detail-context-item">
          <MapPin size={17} aria-hidden="true" />
          <div>
            <span>{t("fields")}</span>
            <strong>{selectedFieldName}</strong>
          </div>
        </div>
        <div className="sensor-detail-context-item">
          <Wifi size={17} aria-hidden="true" />
          <div>
            <span>{t("status", { defaultValue: "Status" })}</span>
            <strong className={hasLiveReading ? "is-live" : "is-muted"}>
              {hasLiveReading
                ? t("online", { defaultValue: "Online" })
                : t("offline", { defaultValue: "Offline" })}
            </strong>
          </div>
        </div>
        <div className="sensor-detail-context-item">
          <BatteryMedium size={17} aria-hidden="true" />
          <div>
            <span>{t("battery", { defaultValue: "Battery" })}</span>
            <strong>
              {hasValue(dataSensor.charge)
                ? `${Math.round(Number(dataSensor.charge))}%`
                : "—"}
            </strong>
          </div>
        </div>
      </section>

      <div className="sensor-detail-stats-grid">
        <section className="sensor-detail-section sensor-detail-section--air">
          <div className="sensor-detail-section-heading">
            <div>
              <span className="sensor-detail-section-kicker">
                {t("overview")}
              </span>
              <h2>{t("sensor_stats_air")}</h2>
            </div>
            <Activity size={19} aria-hidden="true" />
          </div>
          <div className="sensor-detail-metrics">
            {airMetrics.map((metric) => (
              <SensorMetric key={metric.label} {...metric} />
            ))}
          </div>
          <div className="sensor-detail-air-status">
            <span className="sensor-detail-air-status-icon" aria-hidden="true">
              <Radio size={21} />
            </span>
            <div className="sensor-detail-air-status-copy">
              <span className="sensor-detail-air-status-label">
                {t("sensor_data_source", {
                  defaultValue: "Source des mesures environnementales",
                })}
              </span>
              {dataSensor.environment ? (
                <SensorEnvironmentStatus environment={dataSensor.environment} />
              ) : (
                <strong className="sensor-detail-air-status-waiting">
                  {t("waiting_for_environment_data", {
                    defaultValue: "En attente de données environnementales",
                  })}
                </strong>
              )}
              <p>
                {t("sensor_environment_source_help", {
                  defaultValue:
                    "La source active garantit la traçabilité des valeurs de température, d'humidité et de pression affichées.",
                })}
              </p>
            </div>
          </div>
        </section>

        <section className="sensor-detail-section">
          <div className="sensor-detail-section-heading">
            <div>
              <span className="sensor-detail-section-kicker">
                {t("overview")}
              </span>
              <h2>{t("sensor_stats_soil")}</h2>
            </div>
            <Waves size={19} aria-hidden="true" />
          </div>
          <SoilDepthVisualization metrics={soilMetrics} t={t} />
        </section>
      </div>

      {isLoading ? (
        <div className="sensor-detail-state">
          <LoadingSpinner />
        </div>
      ) : hasLiveReading ? (
        <DBSensor filteredSensors={dataSensor} sensorDepths={sensorDepths} />
      ) : (
        <div className="sensor-detail-state">
          <span className="sensor-detail-state-icon">
            <Gauge size={25} aria-hidden="true" />
          </span>
          <h2>{t("no_data", { defaultValue: "No reading available" })}</h2>
          <p>
            {t("no_sensor_data", {
              defaultValue:
                "The selected sensor has not sent a reading for this field yet.",
            })}
          </p>
        </div>
      )}
    </Container>
  );
}
